import { createContext } from 'react';
import { atom } from 'jotai';
import type { Document } from '@/features/documents/types';
import { atomWithStorage } from 'jotai/utils';
import type { ChatTurn } from '@/features/chat/turn';
import type {
  ChatSurface,
  GenericClarification,
  Message,
  QueuedChatMessage,
} from '@/features/chat/types';
import { DocumentTypeName, type DocumentType } from '@/features/documents/constants';
import type { WorkspaceType } from '@/features/workspaces/constants';

export const ChatContext = createContext<{
  threadId: string;
  scope: { id: string; type: DocumentType; typeName: WorkspaceType | 'project' };
  resource: { id: string; type: DocumentType; typeName: DocumentTypeName };
  surface: ChatSurface;
  isThreadReady: boolean;
  documents: Document[];
  documentsPending: boolean;
  isStreamFinished: boolean;
  openedArtifactId: string | null;
  setPageArtifactId: (artifactId: string | null) => void;
  sendMessage: (params: {
    userPrompt: string;
    specifiedDocuments?: string[];
    startNewThread?: boolean;
    analysisParams?: Record<string, unknown>;
    openedArtifactId?: string | null;
  }) => void;
  sendClarification: (data: GenericClarification, label?: string) => void;
  retryMessage: (messageId: string) => void;
  messages: Message[];
  pickThread: (id: string) => void;
  patchMessage: (messageId: string, patch: (message: Message) => Message) => void;
} | null>(null);

export const chatInputAtom = atom<string>('');

export const chatQueueAtom = atom<QueuedChatMessage[]>([]);

export const activeChatTurnAtom = atom<ChatTurn | null>(null);

export const editingQueuedMessageIdAtom = atom<string | null>(null);

export const selectedChatDocumentsAtom = atom<Document[]>([]);

export const chatDialogOpenAtom = atom(false);

export const chatAnnouncementHiddenAtom = atomWithStorage('chat_capabilities_card_hidden', false);

export const chatCreateHintDismissedAtom = atomWithStorage(
  'chat_create_hint_dismissed',
  false,
  undefined,
  { getOnInit: true },
);

export const chatVoiceInputUnavailableAtom = atomWithStorage(
  'chat_voice_input_unavailable',
  false,
  undefined,
  { getOnInit: true },
);

export const chatSidePanelOpenAtom = atomWithStorage('sidechatOpen', true);
export const chatSidePanelWidthAtom = atomWithStorage('sidechatWidth', 0);
export const chatLayoutAtom = atomWithStorage<'left' | 'right' | 'popout'>(
  'sidechatLayout',
  'right',
);
export const chatPopoutOffsetAtom = atomWithStorage(
  'sidechatPopoutOffset',
  { x: 0, y: 0 },
  undefined,
  { getOnInit: true },
);
export const chatPopoutSizeAtom = atomWithStorage(
  'sidechatPopoutSize',
  { width: 400, height: 640 },
  undefined,
  { getOnInit: true },
);

export const activeCellChatAtom = atom<{
  packId?: string;
  columnId?: string;
  cellInstanceId?: string;
  disciplineKey?: string;
  columnTitle?: string;
  workPackageName?: string;
  disciplineName?: string;
} | null>(null);
